import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BlobResponse } from '../../Models/OtherClasses';
import { BaseService } from "../BaseService";
import { UrlService } from "../UrlService";

@Injectable()
export class PickupDeliveryService {
	private url: string;

	constructor(
		private myBaseService: BaseService,
		private urlService: UrlService
	) {
		this.url = urlService.getURL('PickupDelivery');
	}

	loadPending(): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/Pending');
	}
	loadScheduleErrors(): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/PossibleScheduleErrors');
	}
	loadWillCall(searchObj: TooBig4Car.Logic.SearchVM): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.post(searchObj, this.url + '/WillCall', true);
	}
	loadAllMissingReservationData(): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/MissingReservationData');
	}

	loadAllInvalidAddresses(): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/InvalidAddresses');
	}
	loadAllForUploadedFile(id: System.Guid): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/ForUploadedFile/' + (id ?? ''));
	}
	loadAllNeedsRescheduling(): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.loadAll(this.url + '/NeedsRescheduling');
	}
	loadAllUnscheduled(searchVM: TooBig4Car.Logic.SearchVM): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.post(searchVM, this.url + '/Unscheduled', true);
	}

	getOrderSettings(custid: System.Guid): Observable<TooBig4Car.Logic.OrderSettingsVM> {
		return this.myBaseService.loadAll(this.url + '/OrderSettings/' + (custid || ''));
	}

	save(tempItem: TooBig4Car.Logic.CombinedPickupDelivery): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(tempItem, this.url, true);
	}

	saveItems(tempItem: TooBig4Car.Logic.CombinedPickupDelivery): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/SaveItems', true);
	}

	bulkAction(obj: TooBig4Car.Logic.PickupDeliveryBulkActionVM): Observable<any> {
		return this.myBaseService.post(obj, this.url + '/Bulk', true);
	}
	cancelItem(id: System.Guid, cancelObj: TooBig4Car.Logic.PickupDeliveryCancellationVM): Observable<string> {
		return this.myBaseService.post(cancelObj, this.url + '/Cancel/' + (id ||''), true);
	}

	deleteItem(id: System.Guid): Observable<boolean> {
		return this.myBaseService.deleteItem(id, this.url);
	}

	getEdit(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.getEdit(id, this.url);
	}
	getEditItems(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.get(id, this.url + '/EditItems');
	}
	getEditManualPrice(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.get(id, this.url + '/ManualPrice');
	}
	saveManualPrice(tempItem: TooBig4Car.Logic.CombinedPickupDelivery): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/ManualPrice', true);
	}
	get(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.get(id, this.url);
	}
	getManualUpload(id: System.Guid): Observable<TooBig4Car.Logic.ManualOrderUploadVM> {
		return this.myBaseService.getWithParams(id, this.url, '/Upload', false);
	}
	getRemovePending(id: System.Guid): Observable<TooBig4Car.Logic.PickupDeliveryRemovePendingVM> {
		return this.myBaseService.getWithParams(id, this.url, '/RemovePending', false);
	}
	getPickList(id: System.Guid): Observable<BlobResponse> {
		return this.myBaseService.loadBlob(this.url + '/' + (id || '') + '/PickList');
	}
	getPickListForDate(date: Date, addFees: boolean, includePickup: boolean, includeWillCall: boolean): Observable<BlobResponse> {
		return this.myBaseService.loadBlob(this.url
			+ '/PickListForDate?date=' + this.myBaseService.getDateStr(date)
			+ '&includePickupLabels=' + (includePickup ?? false).toString()
			+ '&addFees=' + (addFees ?? false).toString()
			+ '&includeWillCall=' + (includeWillCall ?? false).toString()
		);
	}
	getCustomerPickListForDate(custID: System.Guid, date: Date, includePickup: boolean): Observable<BlobResponse> {
		return this.myBaseService.loadBlob(this.url
			+ '/CustomerPickListForDate/' + custID.toString()
			+ '?date= ' + this.myBaseService.getDateStr(date)
			+ '&includePickupLabels=' + (includePickup ?? false).toString()
		);
	}
	
	getQRCode(id: System.Guid): Observable<BlobResponse> {
		return this.myBaseService.loadBlob(this.url + '/' + (id || '') + '/QR');
	}

	getSignImageText(id: System.Guid): Observable<string> {
		return this.myBaseService.loadAll(this.url + '/' + (id || '') + '/SignatureText');
	}
	getSign(id: System.Guid): Observable<TooBig4Car.Logic.SignatureVM> {
		return this.myBaseService.get(id, this.url + '/Sign');
	}
	sign(tempItem: TooBig4Car.Logic.SignatureVM): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/Sign', true);
	}
	removePending(tempItem: TooBig4Car.Logic.PickupDeliveryRemovePendingVM): Observable<string> {
		return this.myBaseService.post(tempItem, this.url + '/RemovePending', true);
	}
	getAnonEdit(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.getPlain(this.url + '/AnonEdit', 'Failed to Load');
	}
	saveAnon(tempItem: TooBig4Car.Logic.SignatureVM): Observable<string> {
		return this.myBaseService.post(tempItem, this.url + '/AnonEdit', true);
	}

	getTruckEdit(id: System.Guid): Observable<TooBig4Car.Logic.PickupDeliveryTruckChangeVM> {
		return this.myBaseService.get(id, this.url + '/TruckEdit');
	}
	saveTruckEdit(tempItem: TooBig4Car.Logic.PickupDeliveryTruckChangeVM): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/TruckEdit', true);
	}

	getScheduleEdit(id: System.Guid): Observable<TooBig4Car.Logic.PickupDeliveryScheduleVM> {
		return this.myBaseService.get(id, this.url + '/ScheduleEdit');
	}
	saveScheduleEdit(tempItem: TooBig4Car.Logic.PickupDeliveryScheduleVM): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/ScheduleEdit', true);
	}

	reject(tempItem: TooBig4Car.Logic.PickupDeliveryRejectVM): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(tempItem, this.url + '/Reject', true);
	}
	pickup(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(null, this.url + '/Pickup/' + (id || ''), true);
	}
	deliver(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(null, this.url + '/Deliver/' + (id || ''), true);
	}
	startPickup(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(null, this.url + '/PickupStart/' + (id || ''), true);
	}
	startDeliver(id: System.Guid): Observable<TooBig4Car.Logic.CombinedPickupDelivery> {
		return this.myBaseService.post(null, this.url + '/DeliverStart/' + (id || ''), true);
	}
	stagingCopyAndReturn(id: System.Guid): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(null, this.url + '/StagingCopyAndReturn/' + (id || ''), true);
	}
	
	labelSearch(searchObj: TooBig4Car.Logic.SearchVM): Observable<BlobResponse> {
		return this.myBaseService.postBlob(searchObj, this.url + '/LabelSearch');
	}
	search(searchObj: TooBig4Car.Logic.SearchVM): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.post(searchObj, this.url + '/Search', true);
	}
	searchForSuveys(searchObj: TooBig4Car.Logic.SearchVM): Observable<TooBig4Car.Logic.PickupDelivery[]> {
		return this.myBaseService.post(searchObj, this.url + '/SearchForSurveys', true);
	}

	getPricingData(obj: TooBig4Car.Logic.CombinedPickupDelivery): Observable<TooBig4Car.Logic.PickupDeliveryPricingBreakdown> {
		return this.myBaseService.post(obj, this.url + '/PricingInfo', true);
	}
	getPricingDataByID(id: System.Guid): Observable<TooBig4Car.Logic.PickupDeliveryPricingBreakdown> {
		return this.myBaseService.post(null, this.url + '/PricingInfo/' + (id || ''), true);
	}
	getPricingDataForItems(obj: TooBig4Car.Logic.CombinedPickupDelivery): Observable<TooBig4Car.Logic.PickupDeliveryPricingBreakdown> {
		return this.myBaseService.post(obj, this.url + '/ItemsPricingInfo', true);
	}
	manualComplete(obj: TooBig4Car.Logic.ManualOrderCompletionVM): Observable<TooBig4Car.Logic.PickupDelivery> {
		return this.myBaseService.post(obj, this.url + '/ManualComplete', true);
	}
 
	markCancellationHandled(id: System.Guid): Observable<TooBig4Car.Logic.PickupDeliveryPricingBreakdown> {
		return this.myBaseService.post(null, this.url + '/MarkCancellationHandled/' + (id || ''), true);
	}
	fixAddress(obj: TooBig4Car.Logic.AddressFixVM): Observable<TooBig4Car.Logic.Address> {
		return this.myBaseService.post(obj, this.url + '/FixAddress', true);
	}
	sendWindowEmail(obj: TooBig4Car.Logic.PickupDeliveryIDsVM): Observable<any> {
		return this.myBaseService.post(obj, this.url + '/SendWindowEmail', true);
	}
}

